import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { useIsAuthenticated } from 'mf_core/auth';
import { useSiteConfig } from 'mf_core/config';

const IM_LOGIN_START_PATH = '/Site/Login/IdpLogin?ReturnUrl=/Site/Login/ForCep';
const IM_LOGIN_SUCCESS_PATH = '/Site/Login/ForCep';
const ORIGIN = globalThis.location.origin;

const LOG_PREFIX = '[Auto-Login-IM-Online]';

export function AutoLoginImOnlineFrame() {
  const [imLoggedIn, setImLoggedIn] = useState(false);
  const isAuthenticated = useIsAuthenticated();
  const { pathname } = useLocation();
  const isHomeView = pathname === '/my/dashboard';
  const { siteCode } = useSiteConfig();
  const frameRef = useRef<any>(null);
  const [frameSrc, setFrameSrc] = useState('');

  /**
   * When route is `\login` clear the imLoggedIn state
   */
  useEffect(() => {
    if (pathname === '/login') {
      setImLoggedIn(false);
    }
  }, [pathname]);
  /**
   * When isHomeView && isAuthenticated && !imLoggedIn
   * wait 4 second and then start the IM Online Login process in the hidden iframe.
   */
  useEffect(() => {
    let active = true;
    let timeoutId: any = undefined;
    setFrameSrc(''); // start with no hidden frame

    const cleanFn = () => {
      active = false;
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = undefined;
      }
    };

    if (
      isHomeView &&
      isAuthenticated &&
      !imLoggedIn &&
      siteCode !== 'hk' &&
      siteCode !== 'id' &&
      siteCode !== 'th'
    ) {
      timeoutId = setTimeout(() => {
        if (active) {
          // eslint-disable-next-line no-console
          console.log(`${LOG_PREFIX} start`);
          setFrameSrc(IM_LOGIN_START_PATH);
        }
      }, 4000);
    }
    return cleanFn;
  }, [isHomeView, isAuthenticated, imLoggedIn, siteCode]);

  /**
   * When frameSrc is set, wait for frame contentWindow.location
   * to complete the IM Online login process.
   */
  useEffect(() => {
    let active = true;
    let intervalId: any = undefined;

    const cleanFn = () => {
      active = false;
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = undefined;
      }
    };

    if (frameSrc) {
      intervalId = setInterval(() => {
        if (active) {
          const href = getFrameHref(frameRef);
          if (href && href.startsWith(ORIGIN)) {
            const path = href.substring(ORIGIN.length);
            if (path.startsWith(IM_LOGIN_SUCCESS_PATH)) {
              setImLoggedIn(true);
              setFrameSrc('');
              cleanFn();
              // eslint-disable-next-line no-console
              console.log(`${LOG_PREFIX} success`);
            }
          }
        }
      }, 1000);
    }

    return cleanFn;
  }, [frameSrc]);

  /**
   * Render Hidden iframe only when `frameSrc` is set
   */
  return frameSrc ? (
    <iframe
      ref={frameRef}
      src={frameSrc}
      style={{
        border: 'none',
        height: 0,
        position: 'absolute',
        width: 0,
      }}
      title="autoLoginFrame"
    />
  ) : null;
}

/** Returns the href of the iframe contentWindow when  */
function getFrameHref(ref: React.MutableRefObject<any>) {
  let href: string | undefined;
  try {
    href = String(ref.current?.contentWindow.location.href || '');
  } catch (e) {
    // Ignoring "DOMException: Blocked a frame with origin"
    // This occurs during the login process when redirected to okta server.
  }
  return href;
}
